import React, {useState} from 'react'
import {PiChatCircleDotsBold} from 'react-icons/pi'
import {FaRegAddressCard} from 'react-icons/fa'
import {useIntl} from 'react-intl'
import {Modal} from 'react-bootstrap'
import {IoIosClose} from 'react-icons/io'
import {CiCreditCard2} from 'react-icons/ci'
import {PiHandbag} from 'react-icons/pi'

interface UserCardProps {
  variant?: 'red' | 'blue' | 'default'
  status?: string
  actions: {
    primary?: {
      label: string
      onClick: () => void
    }
    secondary?: {
      label: string
      onClick: () => void
    }
    tertiary?: {
      label: string
      onClick: () => void
    }
  }
  data: {
    claimNumber: string
    memberName: string
    cardNumber: string
    policyNumber: string
    createdAt: string
    registeredTime: string
    customerGroup: string
    status: string
    claimType: string
    treatmentType: string
  }
  onChatAction: (claim: any) => void
  onClaimAction: (claim: any) => void
  onDownloadIGL: (claimNumber: string) => void
  onUpdateClaimStatus: (claimNumber: string, remark: string) => void
}

const UserCard: React.FC<UserCardProps> = ({
  data,
  variant = 'default',
  status,
  actions,
  onChatAction,
  onClaimAction,
  onDownloadIGL,
  onUpdateClaimStatus,
}) => {
  const intl = useIntl()
  const {formatMessage: f} = useIntl()
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [remark, setRemark] = useState('')
  const [showRemarkError, setShowRemarkError] = useState(false)
  const [isConfirmCancel, setIsConfirmCancel] = useState(false)

  const getBgColor = () => {
    switch (variant) {
      case 'red':
        return '#F15E60'
      case 'blue':
        return '#059BB6'
      default:
        return '#ffffff'
    }
  }

  const getBorderColor = () => {
    switch (variant) {
      case 'red':
        return '#F59293'
      case 'blue':
        return '#E1E2EA'
      default:
        return '#000000'
    }
  }

  const getButtonColor = () => {
    switch (variant) {
      case 'red':
        return '#F15E60'
      case 'blue':
        return '#059BB6'
      default:
        return '#ffffff'
    }
  }

  const getSvgFillColor = () => {
    switch (variant) {
      case 'red':
        return '#F8D7D7'
      case 'blue':
        return '#CDEBF0'
      default:
        return '#F8F8F8'
    }
  }

  const getBackgroundColor = () => {
    switch (variant) {
      case 'red':
        return '#FEF2F2'
      case 'blue':
        return '#F5FBFC'
      default:
        return '#FFFFFF'
    }
  }

  const getLocalizedMonths = () => {
    if (intl.locale === 'id') {
      return [
        'Januari',
        'Februari',
        'Maret',
        'April',
        'Mei',
        'Juni',
        'Juli',
        'Agustus',
        'September',
        'Oktober',
        'November',
        'Desember',
      ]
    }

    return [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]
  }

  const redSectionStatuses = [
    'Pre-Registered',
    'Pending on IGL',
    'IGL Released',
    'Daily Monitoring',
    'Supercreeding request',
    'Pending on FGL',
  ]

  const formatDate = (dateString: string) => {
    if (!dateString) return ''

    const date = new Date(dateString)
    const months = getLocalizedMonths()

    const day = date.getDate().toString().padStart(2, '0')
    const month = months[date.getMonth()]
    const year = date.getFullYear()
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')

    return `${day}-${month}-${year} ${hours}:${minutes}`
  }

  const handleCancelClick = () => {
    setShowCancelModal(true)
  }

  const handleModalClose = () => {
    setShowCancelModal(false)
  }

  const handleConfirmCancel = () => {
    if (!remark.trim()) {
      setShowRemarkError(true)
      return
    }
    onUpdateClaimStatus(data.claimNumber, remark)
    handleModalClose()
    setRemark('')
    setShowRemarkError(false)
  }

  const renderButtons = () => {
    if (data?.status === 'Pre-Registered') {
      return (
        <>
          <button
            className={`btn py-2 px-4 me-2 cursor-pointer`}
            style={{backgroundColor: getButtonColor(), color: 'white', fontSize: '12px'}}
            onClick={() => onClaimAction(data)}
          >
            {f({id: 'DASHBOARD.GET_INITIAL_GUARANTEE_LETTER'})}
          </button>
          <button
            className='btn btn-light-danger py-2 px-4'
            style={{fontSize: '12px'}}
            onClick={handleCancelClick}
          >
            {f({id: 'DASHBOARD.VOID'})}
          </button>
        </>
      )
    }

    if (data?.status === 'Pending on IGL' || data?.status === 'Pending on FGL') {
      return (
        <button
          className={`btn py-2 px-4 cursor-pointer`}
          style={{backgroundColor: getButtonColor(), color: 'white', fontSize: '12px'}}
          onClick={() => onClaimAction(data)}
        >
          {variant === 'red'
            ? f({id: 'DASHBOARD.RESPOND_TO_QUERY'})
            : f({id: 'DASHBOARD.VIEW_CLAIM'})}
        </button>
      )
    }

    if (
      data?.status === 'IGL RELEASED' ||
      data?.status === 'Daily Monitoring' ||
      data?.status === 'Supercreeding request'
    ) {
      return (
        <>
          <button
            className={`btn py-2 px-4 me-2 cursor-pointer`}
            style={{backgroundColor: getButtonColor(), color: 'white', fontSize: '12px'}}
            onClick={() => onDownloadIGL(data?.claimNumber)}
          >
            {f({id: 'DASHBOARD.DOWNLOAD_IGL'})}
          </button>
          <button
            className='btn btn-light-danger py-2 px-4 cursor-pointer'
            style={{fontSize: '12px'}}
            onClick={handleCancelClick}
          >
            {f({id: 'DASHBOARD.VOID'})}
          </button>
        </>
      )
    }

    return (
      <button
        className={`btn py-2 px-4 cursor-pointer`}
        style={{backgroundColor: getButtonColor(), color: 'white', fontSize: '12px'}}
        onClick={() => onClaimAction(data)}
      >
        {variant === 'red'
          ? f({id: 'DASHBOARD.RESPOND_TO_QUERY'})
          : f({id: 'DASHBOARD.VIEW_CLAIM'})}
      </button>
    )
  }

  return (
    <>
      <div
        className={`card shadow-sm claim-card-hover-${variant}`}
        style={{
          border: `1px solid ${getBorderColor()}`,
          width: '382px',
          height: '255px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <style>
          {`
            .claim-card-hover-red,
            .claim-card-hover-blue,
            .claim-card-hover-default {
              transition: box-shadow 0.3s ease, transform 0.2s ease;
            }
            
            .claim-card-hover-red:hover {
              box-shadow: 0 0.5rem 1rem rgba(241, 94, 96, 0.25) !important;
              transform: translateY(-2px);
            }
            
            .claim-card-hover-blue:hover {
              box-shadow: 0 0.5rem 1rem rgba(5, 155, 182, 0.25) !important;
              transform: translateY(-2px);
            }
            
            .claim-card-hover-default:hover {
              box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
              transform: translateY(-2px);
            }
          `}
        </style>
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: '122px',
            height: '100%',
            backgroundColor: getBackgroundColor(),
            zIndex: 0,
          }}
        ></div>

        <div
          className='d-flex flex-column justify-content-between'
          style={{height: '100%', position: 'relative', zIndex: 1}}
        >
          <div
            style={{
              backgroundColor: getBgColor(),
              height: '46px',
              borderTopLeftRadius: '11px',
              borderTopRightRadius: '11px',
            }}
          ></div>
          <div
            className='card-body d-flex flex-column justify-content-between'
            style={{padding: '0 12px 12px 12px'}}
          >
            <div className='d-flex justify-content-between mb-2'>
              <span
                className='text-gray-600 fs-7 fw-bold badge badge-light-primary d-flex align-items-center'
                style={{
                  position: 'absolute',
                  top: '17px',
                  height: '39px',
                  textDecoration: 'underline',
                  color: '#4B4D63',
                }}
              >
                #{data?.claimNumber}
              </span>
              <span
                className='svg-icon svg-icon-gray-500 badge badge-light-primary rounded-circle'
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  padding: '8px',
                }}
              >
                <PiChatCircleDotsBold
                  size={24}
                  style={{width: '24px', height: '24px', color: '#4B4D63'}}
                  className='cursor-pointer'
                  onClick={() => onChatAction(data)}
                />
              </span>
            </div>

            <h2 className='fw-bolder mb-4 mt-4 px-3' style={{fontSize: '17px'}}>
              {data?.memberName}
            </h2>

            <div className='d-flex mb-3'>
              <span className='d-flex align-items-center px-3 me-2'>
                <CiCreditCard2 size={18} className='me-2' />

                <span className='fs-7 fw-bold'>{data?.cardNumber}</span>
              </span>
            </div>

            <div className='d-flex mb-4'>
              <span className='d-flex align-items-center px-3'>
                <PiHandbag size={18} className='me-2' />
                <span className='fs-7 fw-bold'>{data?.customerGroup}</span>
              </span>
            </div>
            <div className='d-flex justify-content-between px-3'>
              <div className='text-gray-500 fs-7'>
                {f({id: 'DASHBOARD.REGISTERED'}, {date: formatDate(data?.createdAt)})}
              </div>
            </div>
          </div>

          <div className='d-flex justify-content-between p-6 pt-0'>
            <div>{renderButtons()}</div>
          </div>

          <div
            style={{
              position: 'absolute',
              right: '20px',
              top: '36%',
              opacity: '0.3',
              transform: 'translateY(-50%)',
            }}
          >
            {data?.treatmentType === 'Rawat inap' && (
              <svg
                width='162'
                height='105'
                viewBox='0 0 162 105'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M8.07692 0C12.5377 0 16.1538 3.61616 16.1538 8.07692V56.5385H64.6154V16.1538C64.6154 11.6931 68.2315 8.07692 72.6923 8.07692H137.308C143.734 8.07692 149.897 10.6298 154.441 15.174C158.986 19.7181 161.538 25.8813 161.538 32.3077V96.9231C161.538 101.384 157.922 105 153.462 105C149.001 105 145.385 101.384 145.385 96.9231V72.6923H16.1538V96.9231C16.1538 101.384 12.5377 105 8.07692 105C3.61616 105 0 101.384 0 96.9231V8.07692C0 3.61616 3.61616 0 8.07692 0ZM80.7692 56.5385H145.385V32.3077C145.385 30.1656 144.534 28.1112 143.019 26.5964C141.504 25.0817 139.45 24.2308 137.308 24.2308H80.7692V56.5385ZM24.2308 32.3077C24.2308 23.3862 31.4631 16.1538 40.3846 16.1538C49.3061 16.1538 56.5385 23.3862 56.5385 32.3077C56.5385 41.2292 49.3061 48.4615 40.3846 48.4615C31.4631 48.4615 24.2308 41.2292 24.2308 32.3077Z'
                  fill={getSvgFillColor()}
                />
              </svg>
            )}

            {data?.treatmentType === 'Rawat jalan' && (
              <svg
                width='135'
                height='140'
                viewBox='0 0 135 140'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M39.9996 13.9998C39.9996 6.26794 45.969 0 53.3326 0C60.6962 0 66.6656 6.26794 66.6656 13.9998C66.6656 21.7317 60.6962 27.9997 53.3326 27.9997C45.969 27.9997 39.9996 21.7317 39.9996 13.9998ZM45.0493 35.2086C47.321 34.6123 49.7242 35.3112 51.38 37.0498L70.2667 56.8811L88.7732 63.3585C92.266 64.581 94.1537 68.5452 92.9894 72.2128C91.8251 75.8804 88.0498 77.8625 84.5569 76.6399L64.5574 69.64C63.5756 69.2964 62.6835 68.7174 61.9517 67.949L51.1122 56.5674L47.1394 81.5966L64.713 100.049C65.2197 100.581 65.6413 101.195 65.9618 101.868L79.2947 129.868C80.9413 133.326 79.6065 137.531 76.3134 139.259C73.0203 140.988 69.0159 139.587 67.3694 136.129L54.5202 109.145L35.2857 88.9488C33.7633 87.3503 33.0699 85.0782 33.4238 82.8483L38.4188 51.3799L24.1174 55.134L12.214 73.8821C10.1718 77.0988 6.03274 77.968 2.96929 75.8235C-0.0941485 73.6791 -0.921951 69.333 1.12034 66.1164L14.4533 45.1166C15.3732 43.6677 16.7744 42.6309 18.3833 42.2085L45.0493 35.2086ZM37.333 99.3989C40.2785 101.718 40.8754 106.106 38.6663 109.199L18.6669 137.199C16.4578 140.291 12.2792 140.918 9.33379 138.598C6.38835 136.279 5.79141 131.891 8.00049 128.799L28 100.799C30.209 97.7061 34.3876 97.0793 37.333 99.3989Z'
                  fill={getSvgFillColor()}
                />
              </svg>
            )}
          </div>
        </div>
      </div>

      {/* Metronic Cancel Confirmation Modal */}
      <Modal
        id='kt_modal_cancel_claim'
        show={showCancelModal}
        onHide={handleModalClose}
        aria-hidden='true'
        dialogClassName='modal-dialog modal-dialog-centered'
        contentClassName='modal-content rounded'
      >
        <div className='modal-header pb-0 border-0 justify-content-end'>
          <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={handleModalClose}>
            <IoIosClose size={24} />
          </div>
        </div>

        <div className='modal-body scroll-y px-10 px-lg-15 pt-0 pb-15'>
          <div className='mb-13 text-center'>
            <h1 className='mb-3'>{f({id: 'DASHBOARD.MODAL_TITLE'})}</h1>
          </div>

          <div className='d-flex flex-column mb-8'>
            <div className='text-center mb-5'>
              <p className='text-gray-800 fs-6 fw-semibold'>
                {f(
                  {id: 'DASHBOARD.MODAL_DESCRIPTION'},
                  {
                    claimNumber: data?.claimNumber,
                    memberName: data?.memberName,
                  }
                )}
              </p>
            </div>

            {isConfirmCancel && (
              <div className='form-group'>
                <label className='required fw-semibold fs-6 mb-2'>
                  {f({id: 'DASHBOARD.REMARK'})}
                </label>
                <textarea
                  className={`form-control ${showRemarkError ? 'is-invalid' : ''}`}
                  rows={3}
                  value={remark}
                  onChange={(e) => {
                    setRemark(e.target.value)
                    setShowRemarkError(false)
                  }}
                />
                {showRemarkError && (
                  <div className='invalid-feedback'>{f({id: 'DASHBOARD.REMARK_REQUIRED'})}</div>
                )}
              </div>
            )}
          </div>

          <div className='d-flex flex-center flex-row-fluid pt-12'>
            <button type='reset' className='btn btn-light me-3' onClick={handleModalClose}>
              {f({id: 'DASHBOARD.MODAL_CANCEL_BUTTON'})}
            </button>

            {!isConfirmCancel && (
              <button
                type='button'
                className='btn btn-primary'
                onClick={() => setIsConfirmCancel(true)}
              >
                {f({id: 'DASHBOARD.MODAL_CONFIRM_BUTTON'})}
              </button>
            )}

            {isConfirmCancel && (
              <button
                type='button'
                className='btn btn-primary'
                onClick={handleConfirmCancel}
                disabled={!remark.trim()}
              >
                {f({id: 'DASHBOARD.MODAL_SUBMIT_BUTTON'})}
              </button>
            )}
          </div>
        </div>
      </Modal>
    </>
  )
}

export default UserCard
